import { apiClient } from '@/shared/api/apiClient';
import { atom } from 'jotai';
import type {
  AgentCatalogTile,
  AgentConnection,
  AgentConnectionListItem,
} from 'quadratic-shared/typesAndSchemasAgentConnections';

/**
 * Outcome of a verify request. Kept on the active-draft atom so the
 * `update_agent_connection_plan` handler can format a diagnostic message
 * for the AI when the model is iterating on a failed verify.
 */
export type AgentConnectionTestResult = {
  ok: boolean;
  status: number | null;
  statusText: string | null;
  bodyPreview: string | null;
  error: string | null;
  durationMs: number;
};

/**
 * The draft AgentConnection currently being set up by the AI in this chat.
 *
 * Set by `create_agent_connection` and cleared by `finalize_agent_connection`
 * (and abandoned implicitly when the chat ends). Used by downstream tool
 * handlers — `test_agent_connection`, `update_agent_connection_plan`,
 * `start_agent_connection_oauth`, `finalize_agent_connection` — to resolve
 * which draft they're operating on without the AI having to thread the
 * uuid through every call.
 *
 * The shape is intentionally minimal: the AI flow is sequential, the chat
 * pane is the only surface that calls these tools, and there is no UI
 * rendering from this atom. Anything richer (plan, narration, etc.) lives
 * on the persisted connection row and is fetched via `agentConnectionsAtom`.
 */
export type ActiveAgentConnectionDraftState = {
  connectionUuid: string;
  teamUuid: string;
  /** Most recent verify outcome. Carried so the next AI iteration can see it. */
  lastTestResult: AgentConnectionTestResult | null;
  /**
   * Epoch ms when the draft was created. Used to report setup duration
   * (draft created → finalized) in analytics.
   */
  startedAt?: number;
};

export const activeAgentConnectionDraftAtom = atom<ActiveAgentConnectionDraftState | null>(null);

/**
 * True while the current analyst turn is driving agent-connection setup
 * (Add Connection entry points, organic setup tool chains, or an active draft).
 * Cleared on finalize; used to route API calls through the non-billable
 * `AgentConnectionSetup` source until the connection is ready for use.
 */
export const agentConnectionSetupSessionAtom = atom<boolean>(false);

/**
 * Latest `research_agent_connection` output for the in-flight setup session.
 * `create_agent_connection` falls back to these values when the runtime AI
 * omits `planJson` / `apiSchemaJson` / `thumbnailUrl`, and may auto-backfill
 * schema onto the draft row when research produced resources but create did not
 * persist them.
 */
export type LastAgentConnectionResearchState = {
  serviceName: string;
  planJson: string;
  apiSchemaJson: string | null;
  thumbnailUrl: string | null;
  apiSchemaResourceCount: number;
  researchedAt: number;
};

export const lastAgentConnectionResearchAtom = atom<LastAgentConnectionResearchState | null>(null);

/** UUID of the agent connection attached to the current analyst chat. */
export const attachedAgentConnectionUuidAtom = atom<string | undefined>(undefined);

/**
 * One-shot flag set when the user enters the analyst via the "Connect to
 * anything" entry point in the Add Connection menu. The next submitted user
 * message reads-and-clears this flag, attaching `addConnectionIntent: true`
 * to its `Context` so `getMessagesForAI` prepends a NOTE telling the model
 * the user is starting an agent-connection setup. Subsequent turns in the
 * same chat do not need it.
 */
export const pendingAddConnectionIntentAtom = atom<boolean>(false);

// -----------------------------------------------------------------------------
// Team-wide cache of ready agent connections
// -----------------------------------------------------------------------------

type ConnectionsCache = {
  teamUuid: string | null;
  items: AgentConnectionListItem[];
  loading: boolean;
  lastLoadedAt: number | null;
  error: string | null;
};

const emptyCache: ConnectionsCache = {
  teamUuid: null,
  items: [],
  loading: false,
  lastLoadedAt: null,
  error: null,
};

/**
 * Persisted list of Agent Connections for the active team. The analyst and
 * settings tabs read from this; the setup flow writes to it on finalize.
 */
const agentConnectionsCacheAtom = atom<ConnectionsCache>(emptyCache);

/** Read-only view of the cached list. */
export const agentConnectionsAtom = atom((get) => get(agentConnectionsCacheAtom));

/**
 * Refreshes the cache from the API for a given team. Debounced by `force`:
 * without `force`, skips if we loaded within the last 5 seconds.
 */
export const refreshAgentConnectionsAtom = atom(
  null,
  async (get, set, { teamUuid, force = false }: { teamUuid: string; force?: boolean }) => {
    const current = get(agentConnectionsCacheAtom);
    const sameTeam = current.teamUuid === teamUuid;
    if (!force && sameTeam && current.lastLoadedAt && Date.now() - current.lastLoadedAt < 5_000) {
      return current.items;
    }
    set(agentConnectionsCacheAtom, { ...current, loading: true, error: null, teamUuid });
    try {
      const items = await apiClient.agentConnections.list(teamUuid);
      set(agentConnectionsCacheAtom, {
        teamUuid,
        items,
        loading: false,
        lastLoadedAt: Date.now(),
        error: null,
      });
      return items;
    } catch (e: unknown) {
      set(agentConnectionsCacheAtom, {
        ...current,
        teamUuid,
        loading: false,
        error: e instanceof Error ? e.message : 'Failed to load agent connections',
      });
      return [] as AgentConnectionListItem[];
    }
  }
);

/**
 * Upsert an item into the cache without a refetch — used after mutations
 * like finalize / rename / bindSecret so the UI updates immediately.
 */
export const upsertAgentConnectionAtom = atom(null, (get, set, item: AgentConnectionListItem | AgentConnection) => {
  const cache = get(agentConnectionsCacheAtom);
  const listItem: AgentConnectionListItem = {
    uuid: item.uuid,
    name: item.name,
    catalogSlug: item.catalogSlug,
    plan: item.plan,
    apiSchema: item.apiSchema,
    thumbnailUrl: item.thumbnailUrl,
    draftStatus: item.draftStatus,
    lastTestedAt: item.lastTestedAt,
    lastTestStatus: item.lastTestStatus,
    createdDate: item.createdDate,
    updatedDate: item.updatedDate,
    secretBindings: item.secretBindings.map((b) => ({
      id: b.id,
      bindingName: b.bindingName,
      secretName: b.secretName,
      secretKind: b.secretKind ?? 'TOKEN',
    })),
  };
  set(agentConnectionsCacheAtom, {
    ...cache,
    items: [listItem, ...cache.items.filter((c) => c.uuid !== item.uuid)],
    lastLoadedAt: Date.now(),
  });
});

export const removeAgentConnectionAtom = atom(null, (get, set, uuid: string) => {
  const cache = get(agentConnectionsCacheAtom);
  set(agentConnectionsCacheAtom, {
    ...cache,
    items: cache.items.filter((c) => c.uuid !== uuid),
  });
});

// -----------------------------------------------------------------------------
// Agent connection catalog cache
//
// The DB-backed catalog (AgentConnectionCatalogEntry) replaces the static
// `AGENT_CONNECTION_CATALOG` array. The list is small, deployment-wide, and
// rarely changes — load it once per session and cache it on a jotai atom.
// Components that only need the tile for a particular slug read via the
// synchronous `getCatalogTileFromCacheAtom` helper, which never refetches.
// -----------------------------------------------------------------------------

type CatalogCache = {
  items: AgentCatalogTile[];
  bySlug: Map<string, AgentCatalogTile>;
  loading: boolean;
  lastLoadedAt: number | null;
  error: string | null;
};

const emptyCatalogCache: CatalogCache = {
  items: [],
  bySlug: new Map(),
  loading: false,
  lastLoadedAt: null,
  error: null,
};

const agentCatalogCacheAtom = atom<CatalogCache>(emptyCatalogCache);

/** Read-only view of the catalog cache. */
export const agentCatalogAtom = atom((get) => get(agentCatalogCacheAtom));

/**
 * Synchronously look up a catalog tile by slug from whatever's currently
 * cached. Returns undefined if the catalog hasn't been loaded yet — UI that
 * needs deterministic data should `await refreshAgentCatalogAtom` first.
 */
export const getCatalogTileFromCacheAtom = atom((get) => {
  const cache = get(agentCatalogCacheAtom);
  return (slug: string | null | undefined): AgentCatalogTile | undefined => {
    if (!slug) return undefined;
    return cache.bySlug.get(slug);
  };
});

/**
 * Resolve the catalog tile's user-facing label. Admin override wins; falls
 * back to the AI-authored `plan.serviceName`. Used everywhere the catalog
 * tile is rendered (AddConnectionMenu, SetupPanel, ContextBuilder).
 */
export const getCatalogTileDisplayName = (tile: AgentCatalogTile): string => tile.displayName ?? tile.plan.serviceName;

/**
 * Refreshes the catalog cache from the API. Skips refetch if loaded within
 * the last 60s unless `force` is set.
 */
export const refreshAgentCatalogAtom = atom(null, async (get, set, params: { force?: boolean } = {}) => {
  const current = get(agentCatalogCacheAtom);
  if (!params.force && current.lastLoadedAt && Date.now() - current.lastLoadedAt < 60_000) {
    return current.items;
  }
  set(agentCatalogCacheAtom, { ...current, loading: true, error: null });
  try {
    const items = await apiClient.agentCatalog.list();
    const bySlug = new Map<string, AgentCatalogTile>();
    for (const t of items) bySlug.set(t.slug, t);
    set(agentCatalogCacheAtom, { items, bySlug, loading: false, lastLoadedAt: Date.now(), error: null });
    return items;
  } catch (e: unknown) {
    set(agentCatalogCacheAtom, {
      ...current,
      loading: false,
      error: e instanceof Error ? e.message : 'Failed to load agent catalog',
    });
    return [] as AgentCatalogTile[];
  }
});
